import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './HowItWorksSection.css';

interface SubStep {
  title: string;
  description: string;
  video: string;
}

interface Step {
  number: number;
  title: string;
  subSteps: SubStep[];
}

const HowItWorksSection: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [hoveredSubStep, setHoveredSubStep] = useState<number | null>(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  const steps: Step[] = [
    {
      number: 1,
      title: 'Define',
      subSteps: [
        {
          title: 'Prompt-Based Generation',
          description: 'Type your requirements (e.g., area, rooms, style) and Sooru instantly generates intelligent, optimized layouts using DrawGPD technology.',
          video: '/videos/1_1.mp4'
        },
        {
          title: 'Existing Plan Integration',
          description: 'Upload hand-drawn sketches, rough PDFs, or DWGS. Sooru converts them into a editable digital blueprint, preserving your spatial organization.',
          video: '/videos/1_2.mp4'
        },
        {
          title: 'Guided Canvas',
          description: 'Begin with a blank canvas and let Sooru guide you through the design process, offering suggestions, space-planning intelligence, and connection corrections.',
          video: '/videos/1_3.mp4'
        }
      ]
    },
    {
      number: 2,
      title: 'Build',
      subSteps: [
        {
          title: '2D Canvas Structural Editing',
          description: 'Effortlessly add, adjust, or remove all key structural components directly on the canvas, ensuring precise dimensional accuracy for every element.',
          video: '/videos/2_1.mp4'
        },
        {
          title: 'Smart Object Library',
          description: 'Access a rich library of doors, windows, electrical fixtures, plumbing, HVAC, and furniture. Each library is semantically integrated, and AI-aware for placement and sizing.',
          video: '/videos/2_2.mp4'
        },
        {
          title: 'Layout Views',
          description: 'Choose how your workspace looks by toggling measurements or applying a variety of color schemes, allowing for quick, effective, and spatial organization.',
          video: '/videos/2_3.mp4'
        }
      ]
    },
    {
      number: 3,
      title: 'Visualize',
      subSteps: [
        {
          title: 'Full 3D Viewing Control',
          description: 'Seamlessly switch to the view-only 3D model to immediately see how your design looks and feels. Return to the 2D canvas to make all necessary edits.',
          video: '/videos/3_1.mp4'
        },
        {
          title: 'Real-Time Finishes',
          description: 'Instantly apply colors, textures, and materials to walls, floors, and ceilings. Visualize how each material reacts to space, light, and your design style in a fully immersive environment.',
          video: '/videos/3_2.mp4'
        },
        {
          title: 'Interactive 3D Walkthrough',
          description: 'Move beyond static models by entering a fully interactive 3D environment. Walk through the space, adjust your view, and experience your design from every angle and a real sense of presence within your creation. This ensures every spatial detail and layout feels exactly as you intended.',
          video: '/videos/3_3.mp4'
        }
      ]
    },
    {
      number: 4,
      title: 'Deliver',
      subSteps: [
        {
          title: 'Annotations & Markups',
          description: 'Add dimensions, labels, notes, text, symbols, and annotations, making your plans clear, expressive, and ready for submission or client presentation.',
          video: '/videos/4_1.mp4'
        },
        {
          title: 'Seamless 3D Handoff',
          description: 'Export your spatial data in flexible 3D formats designed for professional compatibility. Transfer your textures and animations effortlessly into any standard architectural or design software.',
          video: '/videos/4_2.mp4'
        },
        {
          title: 'Multi-Platform Delivery',
          description: 'Export precise floor plans and elevations in lossless PNG or scalable SVG formats for any presentation scale. Simplify collaboration by sharing live project links that allow others to view your design without specialized software.',
          video: '/videos/4_3.mp4'
        }
      ]
    }
  ];

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 900);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Scroll to the appropriate section position when step changes manually
  const scrollToStep = (stepIndex: number) => {
    if (!sectionRef.current || isMobile) return;
    
    const sectionTop = sectionRef.current.offsetTop;
    const sectionHeight = sectionRef.current.offsetHeight;
    
    // Calculate the scroll position for this step
    const stepProgress = (stepIndex + 0.5) / steps.length; // Center of the step
    const targetScroll = sectionTop + (stepProgress * sectionHeight) - (window.innerHeight / 2);
    
    window.scrollTo({
      top: targetScroll,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    if (isMobile) return; // Don't use scroll on mobile

    const handleScroll = () => {
      if (!sectionRef.current) return;

      const sectionTop = sectionRef.current.offsetTop;
      const sectionHeight = sectionRef.current.offsetHeight;
      const scrollPosition = window.scrollY + window.innerHeight / 2;

      // Determine which step should be active based on scroll position
      const relativeScroll = scrollPosition - sectionTop;
      const stepProgress = relativeScroll / sectionHeight;
      const newActiveStep = Math.min(
        Math.max(0, Math.floor(stepProgress * steps.length)),
        steps.length - 1
      );

      if (newActiveStep !== activeStep) {
        setActiveStep(newActiveStep);
        setHoveredSubStep(0); // Reset to first substep when changing main step
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeStep, isMobile, steps.length]);

  const handlePrevStep = () => {
    if (activeStep > 0) {
      const newStep = activeStep - 1;
      setActiveStep(newStep);
      setHoveredSubStep(0);
      scrollToStep(newStep);
    }
  };

  const handleNextStep = () => {
    if (activeStep < steps.length - 1) {
      const newStep = activeStep + 1;
      setActiveStep(newStep);
      setHoveredSubStep(0);
      scrollToStep(newStep);
    }
  };

  const handleStepClick = (index: number) => {
    setActiveStep(index);
    setHoveredSubStep(0);
    scrollToStep(index);
  };

  // Calculate dot position based on hovered substep
  const getDotPosition = () => {
    if (hoveredSubStep === null) return 0;
    const substepCount = steps[activeStep].subSteps.length;
    const substepHeight = 100 / substepCount;
    return hoveredSubStep * substepHeight + (substepHeight / 2);
  };

  return (
    <section className="how-it-works-new" ref={sectionRef} id="how">
      <div className="how-container">
        <h2 className="how-heading">How it works?</h2>
        
        {/* Step Navigation Dots (Desktop) */}
        {!isMobile && (
          <div className="step-dots-container">
            {steps.map((step, index) => (
              <button
                key={index}
                className={`step-dot ${activeStep === index ? 'active' : ''}`}
                onClick={() => handleStepClick(index)}
                aria-label={`Go to ${step.title}`}
              >
                <span className="dot-number">{step.number}</span>
                <span className="dot-label">{step.title}</span>
              </button>
            ))}
          </div>
        )}

        {/* Mobile Step Selector */}
        {isMobile && (
          <div className="mobile-step-selector">
            <button
              className="step-nav-btn"
              onClick={handlePrevStep}
              disabled={activeStep === 0}
              aria-label="Previous step"
            >
              <ChevronLeft size={20} />
            </button>
            
            <div className="mobile-step-buttons">
              {steps.map((step, index) => (
                <button
                  key={index}
                  className={`mobile-step-btn ${activeStep === index ? 'active' : ''}`}
                  onClick={() => handleStepClick(index)}
                >
                  <span className="mobile-step-number">{step.number}</span>
                  <span className="mobile-step-title">{step.title}</span>
                </button>
              ))}
            </div>

            <button
              className="step-nav-btn"
              onClick={handleNextStep}
              disabled={activeStep === steps.length - 1}
              aria-label="Next step"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}
        
        <div className="how-content">
          {/* Left Side - Active Step Only */}
          <div className="steps-sidebar">
            <div className="step-header">
              <span className="step-number">{steps[activeStep].number}.</span>
              <span className="step-title">{steps[activeStep].title}</span>
            </div>
            
            {/* Sub-steps with blue indicator line and animated dot */}
            <div className="substeps-list">
              <div 
                className="indicator-dot" 
                style={{ top: `${getDotPosition()}%` }}
              />
              {steps[activeStep].subSteps.map((subStep, subIndex) => (
                <div
                  key={subIndex}
                  className={`substep ${hoveredSubStep === subIndex ? 'active' : ''}`}
                  onMouseEnter={() => setHoveredSubStep(subIndex)}
                  onClick={() => setHoveredSubStep(subIndex)}
                >
                  <div className="substep-content">
                    <h3 className="substep-title">{subStep.title}</h3>
                    {hoveredSubStep === subIndex && (
                      <p className="substep-description">{subStep.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - Video Display */}
          <div className="video-display">
            {hoveredSubStep !== null && (
              <video
                key={`${activeStep}-${hoveredSubStep}`}
                className="feature-video"
                autoPlay
                loop
                muted
                playsInline
                controls
                controlsList="nodownload"
                preload="auto"
              >
                <source src={steps[activeStep].subSteps[hoveredSubStep].video} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;